<form
  [formGroup]="form()"
  class="grid grid-cols-1 gap-1"
  [class.lg:grid-cols-[3fr_1fr]]="!singleColumn() && wideRatio()"
  [class.lg:grid-cols-3]="!singleColumn() && !wideRatio()">
  @for (item of formBuilder(schema()); track trackFn(item)) {
    @if ('type' in item) {
      @if (isImmutableLocked(item.id, item.immutable)) {
        <div class="border-weak flex flex-col rounded-sm border opacity-60">
          <div class="flex h-8 items-center p-2 text-xs">
            <span>
              {{ item.label[currentLang()] }}
              @if (item.required) {
                <span class="ml-1 text-red-500">*</span>
              }
            </span>
          </div>
          <div class="flex h-full flex-col gap-3 rounded-sm p-2">
            <div
              class="border-default bg-surface text-text-secondary block w-full cursor-not-allowed rounded-lg border p-2.5 text-sm select-none">
              {{ $any(form().get(item.id)?.value ?? values[item.id] ?? '') }}
            </div>
            <app-hidden
              [formControlName]="item.id"
              [id]="item.id"
              [value]="$any(values[item.id] ?? null)"
              (valueLoaded)="checkMultiplayItem($event)" />
          </div>
        </div>
      } @else {
        <div
          class="border-weak flex flex-col rounded-sm border"
          [class.lg:col-span-full]="item.type === FormBuilderFieldType.COMPATIBILITY_CFG">
          <div class="flex h-8 items-center justify-between p-2 text-xs">
            <span>
              {{ item.label[currentLang()] }}
              @if (item.required) {
                <span class="ml-1 text-red-500">*</span>
              }
            </span>
            <div class="flex items-center">
              @if (item.type === FormBuilderFieldType.MULTIPLAY) {
                <div
                  class="flex rounded bg-zinc-200 p-0.5 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  @for (_ of [].constructor(item.oneOf.length); let i = $index; track i) {
                    <span
                      [tabindex]="i"
                      (keydown)="setMultiplayIndex(item.id, i)"
                      (click)="setMultiplayIndex(item.id, i)"
                      [class.bg-white]="activatedMultiplayIndices[item.id] === i"
                      [class.dark:bg-zinc-900]="activatedMultiplayIndices[item.id] === i"
                      class="flex cursor-pointer rounded px-1 py-0.5 capitalize">
                      {{ activatedMultiplayLabels[item.id][i] }}
                    </span>
                  }
                </div>
              }
              @if (item.description) {
                <span #fieldTooltip class="group relative inline-block">
                  <i
                    [class]="ICONS.info"
                    class="text-text-tertiary group-hover:text-text-primary ml-2 cursor-pointer">
                  </i>
                </span>
                <app-tooltip
                  [target]="fieldTooltip"
                  [title]="item.label[currentLang()]"
                  [content]="item.description[currentLang()]"
                  [position]="'top'"
                  [minWidth]="200"
                  [maxWidth]="390" />
              }
            </div>
          </div>
          <div
            class="flex h-full flex-col gap-3 rounded-sm p-2"
            [ngClass]="{
              'bg-surface': !inputHasError(item.id),
              'bg-red-200 dark:bg-red-800': inputHasError(item.id)
            }">
            @switch (item.type) {
              @case (FormBuilderFieldType.STRING) {
                <app-string
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])"
                  [minLength]="item.minLength"
                  [maxLength]="item.maxLength"
                  [minlength]="item.minLength ?? null"
                  [maxlength]="item.maxLength ?? null" />
              }
              @case (FormBuilderFieldType.DATE) {
                <app-date
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])"
                  [needTime]="item.needTime ?? false"
                  [isArray]="item.isArray"
                  [min]="item.minDate"
                  [max]="item.maxDate" />
              }
              @case (FormBuilderFieldType.BOOLEAN) {
                <app-boolean
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])"
                  [indeterminate]="item.indeterminate" />
              }
              @case (FormBuilderFieldType.NUMBER) {
                <app-number
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])"
                  [min]="item.min"
                  [max]="item.max"
                  [step]="item.step" />
              }
              @case (FormBuilderFieldType.ITEMS) {
                <app-items
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])"
                  [possibleValues]="item.values" />
              }
              @case (FormBuilderFieldType.TIME) {
                <app-time
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.CONST) {
                <app-const
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(item.const)" />
              }
              @case (FormBuilderFieldType.DURATION) {
                <app-duration
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.AUTO_COMPLETE) {
                <app-multi-autocomplete
                  [formControlName]="item.id"
                  [id]="item.id"
                  [options]="item.options"
                  [allowCustomValues]="item.allowCustomValues"
                  [customValueConfig]="item.customValueConfig" />
              }
              @case (FormBuilderFieldType.ENTITY_AUTO_COMPLETE) {
                <app-entity-auto-complete
                  [formControlName]="item.id"
                  [id]="item.id"
                  [required]="item.required"
                  [entityType]="item.entityType"
                  [ownerEntityType]="entityType()"
                  [isArray]="item.isArray"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.CUSTOM_MAP) {
                <app-custom-map
                  [formControlName]="item.id"
                  [id]="item.id"
                  [valueTypes]="item.valueTypes"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.TAGS_INPUT) {
                <app-tags-input
                  [formControlName]="item.id"
                  [id]="item.id"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.LABELS_MAP) {
                <app-labels-map
                  [formControlName]="item.id"
                  [id]="item.id"
                  [entityType]="entityType()"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.TEXT_LIST) {
                <app-text-list-input
                  [formControlName]="item.id"
                  [id]="item.id"
                  [value]="$any(values[item.id])" />
              }
              @case (FormBuilderFieldType.GEOJSON_POLYGON) {
                <app-geojson-polygon
                  [formControlName]="item.id"
                  [id]="item.id"
                  [entityUid]="rowId()" />
              }
              @case (FormBuilderFieldType.POLYLINE) {
                <app-polyline
                  [formControlName]="item.id"
                  [id]="item.id"
                  [entityUid]="rowId()" />
              }
              @case (FormBuilderFieldType.COLOR) {
                <app-color-picker
                  [formControlName]="item.id"
                  [clearable]="!item.required" />
              }
              @case (FormBuilderFieldType.COMPATIBILITY_CFG) {
                <app-compatibility-cfg-field
                  [formControlName]="item.id"
                  [carrierSchema]="item.carrierSchema"
                  [entityType]="entityType()" />
              }
              @case (FormBuilderFieldType.MULTIPLAY) {
                @if (getMultiplayIndex(item.id) === -1) {
                  <app-hidden
                    [formControlName]="item.id"
                    [id]="item.id"
                    [required]="item.required"
                    [value]="''"
                    (valueLoaded)="checkMultiplayItem($event)" />
                }
                @if (
                  item.oneOf && item.oneOf.length > 0 && getMultiplayIndex(item.id) >= 0
                ) {
                  @let subItem = item.oneOf[getMultiplayIndex(item.id)];
                  @switch (subItem.type) {
                    @case (FormBuilderFieldType.STRING) {
                      <app-string
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])"
                        [minLength]="subItem.minLength"
                        [maxLength]="subItem.maxLength"
                        [minlength]="subItem.minLength ?? null"
                        [maxlength]="subItem.maxLength ?? null" />
                    }
                    @case (FormBuilderFieldType.DATE) {
                      <app-date
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])"
                        [needTime]="subItem.needTime ?? false"
                        [min]="subItem.minDate"
                        [max]="subItem.maxDate" />
                    }
                    @case (FormBuilderFieldType.BOOLEAN) {
                      <app-boolean
                        [formControlName]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [id]="item.id"
                        [required]="item.required"
                        [value]="$any(values[item.id])"
                        [indeterminate]="subItem.indeterminate" />
                    }
                    @case (FormBuilderFieldType.DURATION) {
                      <app-duration
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])" />
                    }
                    @case (FormBuilderFieldType.NUMBER) {
                      <app-number
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])"
                        [min]="subItem.min"
                        [max]="subItem.max"
                        [step]="subItem.step" />
                    }
                    @case (FormBuilderFieldType.ITEMS) {
                      <app-items
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])"
                        [possibleValues]="subItem.values" />
                    }
                    @case (FormBuilderFieldType.TIME) {
                      <app-time
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [required]="item.required"
                        [value]="$any(values[item.id])" />
                    }
                    @case (FormBuilderFieldType.CONST) {
                      @if (subItem.const !== null) {
                        <app-const
                          [formControlName]="item.id"
                          [wrapperProperty]="
                            subItem.id !== item.id ? subItem.id : undefined
                          "
                          [id]="item.id"
                          [required]="item.required"
                          [value]="$any(subItem.const)" />
                      } @else {
                        <span class="text-text-tertiary px-1 text-xs italic">
                          {{ 'common.none' | translate }}
                        </span>
                      }
                    }
                    @case (FormBuilderFieldType.AUTO_COMPLETE) {
                      <app-multi-autocomplete
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [allowCustomValues]="subItem.allowCustomValues"
                        [customValueConfig]="subItem.customValueConfig"
                        [options]="subItem.options" />
                    }
                    @case (FormBuilderFieldType.ENTITY_AUTO_COMPLETE) {
                      <app-entity-auto-complete
                        [formControlName]="item.id"
                        [id]="item.id"
                        [required]="item.required"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [entityType]="subItem.entityType"
                        [ownerEntityType]="entityType()"
                        [isArray]="item.isArray"
                        [value]="$any(values[item.id])" />
                    }
                    @case (FormBuilderFieldType.CUSTOM_MAP) {
                      <app-custom-map
                        [formControlName]="item.id"
                        [id]="item.id"
                        [wrapperProperty]="subItem.id !== item.id ? subItem.id : undefined"
                        [valueTypes]="subItem.valueTypes"
                        [value]="$any(values[item.id])" />
                    }
                    @case (FormBuilderFieldType.TAGGED_SECTION) {
                      <app-tagged-section
                        [formControlName]="item.id"
                        [fields]="subItem.fields"
                        [discriminatorField]="subItem.discriminatorField"
                        [discriminatorValue]="subItem.discriminatorValue"
                        [formMode]="formMode()"
                        [rowId]="rowId()" />
                    }
                    @default {
                      <!--
                        Section/vector variant of an externally-tagged enum
                        (e.g. job_visits → { job_visit_patterns: [...] }). Sections
                        have no `type`; any other unmatched variant keeps the
                        previous behaviour of rendering nothing. The envelope
                        FormGroup lives at form.get(item.id); delegate to a nested
                        form-builder so the existing section/array UI (add/remove,
                        nested recursion) is fully reused.
                      -->
                      @if (!$any(subItem).type) {
                        <app-form-builder
                          [schema]="{ items: [$any(subItem)] }"
                          [form]="$any(form().get(item.id))"
                          [actualValues]="$any(values[item.id])"
                          [wideRatio]="wideRatio() || startsWideRatio(item)"
                          [entityType]="entityType()"
                          [rowId]="rowId()"
                          [formMode]="formMode()" />
                      }
                    }
                  }
                }
              }
            }
            @if (inputHasError(item.id)) {
              <div class="flex shrink-0 items-center gap-1 text-red-500 dark:text-red-300">
                <i [class]="ICONS.warningCircle"></i>
                <span class="ml-1 text-sm">
                  {{
                    'form.error.' + getInputErrorCode(item.id)
                      | translate: getInputErrorParams(item.id)
                  }}
                </span>
              </div>
            }
          </div>
        </div>
      }
    } @else {
      <div
        class="border-default my-1 overflow-hidden rounded-md border"
        [class.lg:col-span-3]="!wideRatio()"
        [class.lg:col-span-2]="wideRatio()">
        <div
          class="flex cursor-pointer items-center justify-between border-zinc-200 bg-zinc-100 p-2 dark:border-zinc-700 dark:bg-zinc-800"
          [class.border-b]="isSectionExpanded(item.id)"
          (keydown)="toggleSection(item.id)"
          (click)="toggleSection(item.id)">
          <div class="flex h-full items-center justify-start">
            <h3 class="mr-2 text-sm font-medium">
              {{ item.label[currentLang()] }}
            </h3>
            @if (item.description) {
              <span #sectionTooltip class="group relative flex items-center">
                <i
                  [class]="ICONS.info"
                  class="text-text-tertiary group-hover:text-text-primary mr-2 cursor-pointer">
                </i>
              </span>
              <app-tooltip
                [target]="sectionTooltip"
                [title]="item.label[currentLang()]"
                [content]="item.description[currentLang()]"
                [position]="'top'"
                [minWidth]="200"
                [maxWidth]="390" />
            }
            @if (!item.isArray) {
              <app-form-switch
                class="h-5"
                [form]="$any(form().get(item.id))"
                [formMode]="formMode()"
                (selectionChanged)="onSectionEnableOrDisable($event, item.id)" />
            }
            @if (form().get(item.id)?.invalid && form().get(item.id)?.touched) {
              <span class="h-5 text-red-500" title="{{ 'form.section_error' | translate }}">
                <i [class]="ICONS.warning"></i>
              </span>
            }
            @if (item.isArray) {
              <span
                class="ml-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-800 dark:bg-blue-900 dark:text-blue-300">
                {{ 'form.array' | translate }}
              </span>
            }
          </div>
          <div class="flex items-center gap-2">
            @if (item.isArray && isSectionExpanded(item.id)) {
              <button
                type="button"
                class="rounded bg-green-600 px-2 py-1 text-xs text-white hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600"
                (click)="addArrayItem($event, item.id)">
                <i [class]="ICONS.plus" class="mr-1"></i>
                {{ 'common.add' | translate }}
              </button>
            }
            <i
              class="duration-slow transition-transform"
              [class.rotate-180]="isSectionExpanded(item.id)">
              ▼
            </i>
          </div>
        </div>

        @if (isSectionExpanded(item.id)) {
          <div class="bg-zinc-50 p-2 dark:bg-zinc-900">
            @if (item.isArray) {
              @for (
                arrayItem of getArrayItems(item.id);
                track arrayItem;
                let idx = $index
              ) {
                <div class="my-2 rounded border border-zinc-300 p-3 dark:border-zinc-600">
                  <div class="mb-2 flex items-center justify-between">
                    <span class="text-text-secondary text-sm font-medium">
                      {{ 'common.item' | translate }} {{ idx + 1 }}
                    </span>
                    <button
                      type="button"
                      class="rounded bg-red-600 px-2 py-1 text-xs text-white hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-600"
                      (click)="removeArrayItem(item.id, idx)">
                      <i [class]="ICONS.trash" class="mr-1"></i>
                      {{ 'common.delete' | translate }}
                    </button>
                  </div>
                  <app-form-builder
                    [schema]="{ items: item.items }"
                    [actualValues]="$any(values[item.id])"
                    [form]="arrayItem"
                    [wideRatio]="wideRatio() || startsWideRatio(item)"
                    [entityType]="entityType()"
                    [rowId]="rowId()"
                    [formMode]="formMode()">
                  </app-form-builder>
                </div>
              } @empty {
                <p class="text-text-tertiary text-center text-sm">
                  {{ 'common.no_items' | translate }}
                </p>
              }
            } @else {
              <app-form-builder
                [schema]="item"
                [form]="$any(form().get(item.id))"
                [actualValues]="$any(values[item.id])"
                [wideRatio]="wideRatio() || startsWideRatio(item)"
                [entityType]="entityType()"
                [rowId]="rowId()"
                [formMode]="formMode()" />
            }
          </div>
        }
      </div>
    }
  }
</form>
